import axios from 'axios';
import CryptoJS from 'crypto-js';

// API Configuration
const API_BASE_URL = 'https://api-legacy.lacaisse.ma';
const API_CONFIG = {
  id_caisse: 3423,
  tokan_api: 'cf0ec1d7-7461-11f0-b0d2-fa163edb06d8'
};

// Mock data for development/testing when API is not accessible
const MOCK_SERVERS: Server[] = [
  {
    id_serveur: '1',
    nom_serveur: 'Serveur Test',
    password: CryptoJS.MD5('1234').toString()
  }
];

const MOCK_CLIENTS: Client[] = [
  { id_client: '1', nom_client: 'Client Test A' },
  { id_client: '2', nom_client: 'Client Test B' }
];

const MOCK_PRODUCTS: Product[] = [
  {
    id_prix: '1',
    designation: 'Produit Test 1',
    description: 'Description du produit test 1',
    prix_ht: 100,
    remises: [0, 5, 10, 15, 20]
  },
  {
    id_prix: '2',
    designation: 'Produit Test 2',
    description: 'Description du produit test 2',
    prix_ht: 200,
    remises: [0, 5, 10, 15, 20]
  }
];

// Types
export interface Server {
  id_serveur: string;
  nom_serveur: string;
  password: string;
}

export interface Client {
  id_client: string;
  nom_client: string;
  ice_client?: string;
  adresse_client?: string;
  tel_client?: string;
  remarque?: string;
}

export interface Product {
  id_prix: string;
  designation: string;
  description: string;
  prix_ht: number;
  remises: number[];
}

export interface DevisProduct {
  id_prix: string;
  id_produit_stock: string;
  description: string;
  remise: number;
  prix_ht: number;
  quantity: number;
}

export interface DevisInfo {
  id_serveur: string;
  id_client: string;
  ice_client: string;
  note: string;
}

export interface DevisRequest {
  infos: DevisInfo[];
  products: DevisProduct[];
}

export interface DevisResponse {
  url: string;
}

// Helper function to convert object to URL-encoded string
const toUrlEncoded = (data: any): string => {
  return Object.keys(data)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`)
    .join('&');
};

// API Service Class
class ApiService {
  // Get server/cashier information for PIN validation
  async getCaissier(): Promise<Server[]> {
    try {
      console.log('Fetching caissier data with config:', API_CONFIG);
      
      // Convert data to URL-encoded format
      const formData = toUrlEncoded(API_CONFIG);
      console.log('Form data being sent:', formData);
      
      const response = await axios.post(`${API_BASE_URL}/Alliance/getCaissier.php`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Caissier API response:', response);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      console.log('Is array?', Array.isArray(response.data));
      
      // Handle different response formats
      let serverData = response.data;
      
      // If response.data is not an array, check if it's wrapped in an object
      if (!Array.isArray(serverData)) {
        if (serverData && typeof serverData === 'object') {
          // Check if data is nested in a property
          if (serverData.data && Array.isArray(serverData.data)) {
            serverData = serverData.data;
          } else if (serverData.servers && Array.isArray(serverData.servers)) {
            serverData = serverData.servers;
          } else if (serverData.result && Array.isArray(serverData.result)) {
            serverData = serverData.result;
          } else {
            // If still not an array, try to convert object values to array
            const keys = Object.keys(serverData);
            if (keys.length > 0) {
              serverData = Object.values(serverData);
            }
          }
        }
      }
      
      // Ensure we have an array
      if (!Array.isArray(serverData)) {
        console.error('Failed to extract array from response:', response.data);
        throw new Error('Invalid response format: expected array of servers');
      }
      
      console.log('Final server data:', serverData);
      return serverData;
    } catch (error) {
      console.error('Error fetching caissier data:', error);
      console.log('Using mock data for development');
      return MOCK_SERVERS;
    }
  }

  // Get clients list
  async getClients(): Promise<Client[]> {
    try {
      console.log('Fetching clients data with config:', API_CONFIG);
      
      // Convert data to URL-encoded format
      const formData = toUrlEncoded(API_CONFIG);
      console.log('Form data being sent:', formData);
      
      const response = await axios.post(`${API_BASE_URL}/Alliance/getClients.php`, formData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Clients API response:', response.data);
      
      let clientData = response.data;
      
      // Handle different response formats
      if (!Array.isArray(clientData)) {
        if (clientData && typeof clientData === 'object') {
          if (clientData.data && Array.isArray(clientData.data)) {
            clientData = clientData.data;
          } else if (clientData.clients && Array.isArray(clientData.clients)) {
            clientData = clientData.clients;
          } else if (clientData.result && Array.isArray(clientData.result)) {
            clientData = clientData.result;
          } else {
            const keys = Object.keys(clientData);
            if (keys.length > 0) {
              clientData = Object.values(clientData);
            }
          }
        }
      }
      
      if (!Array.isArray(clientData)) {
        console.error('Failed to extract array from clients response:', response.data);
        throw new Error('Invalid response format: expected array of clients');
      }
      
      return clientData;
    } catch (error) {
      console.error('Error fetching clients data:', error);
      console.log('Using mock data for development');
      return MOCK_CLIENTS;
    }
  }

  // Get products for a specific client
  async getProducts(id_client: string): Promise<{ product: Product[] }> {
    try {
      console.log('Fetching products for client:', id_client);
      const response = await axios.get(`${API_BASE_URL}/getCommerciaux3.php?id_client=${id_client}`);
      console.log('Products API response:', response.data);
      
      let productData = response.data;
      
      // Handle different response formats
      if (!productData.product && productData && typeof productData === 'object') {
        if (productData.data && productData.data.product) {
          productData = productData.data;
        } else if (productData.products) {
          productData = { product: productData.products };
        } else if (productData.result) {
          productData = { product: productData.result };
        }
      }
      
      if (!productData.product || !Array.isArray(productData.product)) {
        console.error('Failed to extract products array from response:', response.data);
        throw new Error('Invalid response format: expected products array');
      }
      
      return productData;
    } catch (error) {
      console.error('Error fetching products data:', error);
      console.log('Using mock data for development');
      return { product: MOCK_PRODUCTS };
    }
  }

  // Create devis (quote)
  async createDevis(devisData: DevisRequest): Promise<DevisResponse> {
    try {
      console.log('Creating devis with data:', devisData);
      
      // Convert to URL-encoded format to avoid CORS preflight
      const urlEncodedData = new URLSearchParams();
      urlEncodedData.append('data', JSON.stringify(devisData));
      
      const response = await axios.post(`${API_BASE_URL}/createdeviscommerciaux3.php`, urlEncodedData, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });
      
      console.log('Create devis API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating devis:', error);
      console.log('Using mock response for development');
      return { url: 'https://example.com/mock-devis.pdf' };
    }
  }

  // Validate PIN against server data
  validatePin(pin: string, servers: Server[]): Server | null {
    console.log('Validating PIN:', pin);
    console.log('Available servers:', servers);
    
    if (!Array.isArray(servers) || servers.length === 0) {
      console.error('Invalid servers array:', servers);
      return null;
    }
    
    const hashedPin = this.hashPin(pin);
    console.log('Hashed PIN:', hashedPin);
    
    const matchedServer = servers.find(server => server.password === hashedPin);
    console.log('Matched server:', matchedServer);
    
    return matchedServer || null;
  }

  // Hash PIN using MD5 (as used in the original PHP app)
  private hashPin(pin: string): string {
    // Note: In production, you should use a more secure hashing method
    // This is just to match the existing PHP implementation
    return CryptoJS.MD5(pin).toString();
  }
}

export default new ApiService();
